/**
 * Keeping a browser signed in, for screens nobody touches (a war-room kiosk).
 *
 * A browser that signed in stays "remembered" until someone signs out
 * explicitly. When its session ends (the access token expired and could not be
 * renewed, or the browser restarted on an expired one), the login page signs in
 * again on its own and returns to the page that was open. Recovery is limited
 * to three attempts, with redirects spaced by {@link AUTO_SIGNIN_INTERVAL_MS}.
 */

const REMEMBERED_KEY = "nexus.auth.remembered";
const LAST_AUTO_SIGNIN_KEY = "nexus.auth.lastAutoSignin";
const RECOVERY_ATTEMPTS_KEY = "nexus.auth.recoveryAttempts";

/** Minimum time between two automatic sign-in attempts. */
export const AUTO_SIGNIN_INTERVAL_MS = 5 * 60_000;
export const MAX_RECOVERY_ATTEMPTS = 3;
export const RECOVERY_TIMEOUT_MS = 30_000;

/** Delays between retries of a failed background renewal, the last one repeating. */
export const RENEW_RETRY_DELAYS_MS = [30_000, 60_000, 2 * 60_000, 5 * 60_000] as const;

/** Where to go after signing in, carried in router state and in the sign-in request. */
export interface ReturnState {
  returnTo?: string;
  /** Set when the session ended on its own, rather than never having started. */
  expired?: boolean;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the session is simply not remembered.
  }
}

export function rememberSession() {
  write(REMEMBERED_KEY, "1");
  write(RECOVERY_ATTEMPTS_KEY, null);
}

/** Called on explicit sign-out: this browser must not sign in again by itself. */
export function forgetSession() {
  write(REMEMBERED_KEY, null);
  write(LAST_AUTO_SIGNIN_KEY, null);
  write(RECOVERY_ATTEMPTS_KEY, null);
}

export function recoveryAttemptCount(): number {
  const count = Number(read(RECOVERY_ATTEMPTS_KEY));
  return Number.isSafeInteger(count) && count > 0 ? count : 0;
}

export function recordRecoveryAttempt(): number {
  const count = recoveryAttemptCount() + 1;
  write(RECOVERY_ATTEMPTS_KEY, String(count));
  return count;
}

export function stopSigninRecovery() {
  write(RECOVERY_ATTEMPTS_KEY, String(MAX_RECOVERY_ATTEMPTS));
}

export function isSessionRemembered(): boolean {
  return read(REMEMBERED_KEY) === "1";
}

export function recordAutoSignin(now = Date.now()) {
  write(LAST_AUTO_SIGNIN_KEY, String(now));
}

/** How long to wait before the next automatic sign-in may start (0: now). */
export function autoSigninDelay(now = Date.now()): number {
  const last = Number(read(LAST_AUTO_SIGNIN_KEY));
  if (!Number.isFinite(last) || last <= 0 || last > now) return 0;
  return Math.max(0, last + AUTO_SIGNIN_INTERVAL_MS - now);
}

export function renewRetryDelay(attempt: number): number {
  return RENEW_RETRY_DELAYS_MS[Math.min(attempt, RENEW_RETRY_DELAYS_MS.length - 1)];
}

export function isBlockingAuthentication(auth: {
  isLoading: boolean;
  isAuthenticated: boolean;
  activeNavigator?: string;
  user?: unknown;
}): boolean {
  return (
    auth.isLoading &&
    (auth.activeNavigator !== "signinSilent" || !auth.isAuthenticated || !auth.user)
  );
}

export function requiresSignin(error: unknown): boolean {
  if (error && typeof error === "object" && "innerError" in error) {
    error = error.innerError;
  }
  const code = error && typeof error === "object" && "error" in error ? error.error : undefined;
  return (
    typeof code === "string" &&
    [
      "invalid_grant",
      "login_required",
      "interaction_required",
      "consent_required",
      "account_selection_required",
    ].includes(code)
  );
}

const AUTH_PATHS = ["/login", "/postlogin"];

/** A path of this application to return to; anything else (another origin, the login pages) is refused. */
export function safeReturnTo(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return undefined;
  }
  if (value.includes("\\")) return undefined;
  const pathname = value.split(/[?#]/, 1)[0];
  if (AUTH_PATHS.includes(pathname)) return undefined;
  return value;
}

/** The return path carried by a router or sign-in state, if any. */
export function returnToFromState(state: unknown): string | undefined {
  if (!state || typeof state !== "object" || !("returnTo" in state)) return undefined;
  return safeReturnTo(state.returnTo);
}

export function pathOf(location: { pathname: string; search?: string; hash?: string }): string {
  return `${location.pathname}${location.search ?? ""}${location.hash ?? ""}`;
}
